import { TrendingDown, Truck } from "@/components/icons";
import { Skeleton } from "@/components/kit";
import type { ShippingPerformanceResultDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";

type ShippingOverviewStripProps = {
  result?: ShippingPerformanceResultDTO;
  isLoading?: boolean;
};

function calcOverallReturnRate(meta?: ShippingPerformanceResultDTO["meta"]) {
  if (!meta || meta.totalShipped <= 0) return "0";
  return ((meta.totalReturned / meta.totalShipped) * 100).toFixed(1);
}

export function ShippingOverviewStrip({
  result,
  isLoading,
}: ShippingOverviewStripProps) {
  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-3">
        {["a", "b", "c"].map((k) => (
          <Skeleton key={k} className="h-22 rounded-2xl" />
        ))}
      </div>
    );
  }

  const meta = result?.meta;
  const overallRate = calcOverallReturnRate(meta);
  const worst = result?.data?.[0];
  const rateNum = Number(overallRate);

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="brand-card flex items-center gap-3 p-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
            <TrendingDown />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-muted-foreground text-xs">ອັດຕາຕີກັບລວມ</span>
            <span
              className={`font-display text-2xl tabular-nums leading-tight ${
                rateNum >= 15
                  ? "text-destructive"
                  : rateNum >= 8
                    ? "text-amber-700 dark:text-amber-400"
                    : ""
              }`}
            >
              {overallRate}%
            </span>
            <span className="text-muted-foreground text-xs">
              ຕີກັບ {meta?.totalReturned ?? 0} / ຈັດສົ່ງ {meta?.totalShipped ?? 0}
            </span>
          </div>
        </div>

        <div className="brand-card flex items-center gap-3 p-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-secondary/15 text-secondary">
            <Truck />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-muted-foreground text-xs">ຂົນສົ່ງຕີກັບສູງສຸດ</span>
            <span className="truncate font-display text-lg leading-tight">
              {worst?.shippingCarrier ?? "—"}
            </span>
            <span className="text-muted-foreground text-xs">
              {worst
                ? `${worst.returnRatePercent}% · ${worst.shippingBranch ?? "ບໍ່ລະບຸສາຂາ"}`
                : "ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້"}
            </span>
          </div>
        </div>

        <div className="brand-card flex items-center gap-3 p-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent/40 text-foreground">
            <TrendingDown />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-muted-foreground text-xs">ຄ່າສົ່ງເສຍລວມ</span>
            <span className="font-display text-2xl tabular-nums leading-tight">
              {formatLak(meta?.totalLoss ?? "0")}
            </span>
            <span className="text-muted-foreground text-xs">
              ຈາກອໍເດີ້ຕີກັບໃນຊ່ວງ
            </span>
          </div>
        </div>
      </div>
      <p className="text-muted-foreground text-xs">
        ອັດຕາຕີກັບ = ຈຳນວນຕີກັບ ÷ ຈຳນວນຈັດສົ່ງ × 100
      </p>
    </div>
  );
}
